import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { StoreBuildModel } from '../../../../models/store-build';
import { snappingService } from '../../../../services/SnappingService';
import { EnvironmentModel } from '../../StoreBuilderDetailsView/environments';
import { SELECTION_COLOR, SELECTION_OPACITY } from '../constants';
import {
  addSelectionOverlay,
  removeSelectionOverlay,
  updateShopperBadgeSelection,
} from '../meshOperations';
import { ShopperMesh, ThreeRefs } from '../types';

interface UseMouseInteractionsProps {
  mountRef: React.RefObject<HTMLDivElement>;
  refs: ThreeRefs;
  build: StoreBuildModel | null;
  environment: EnvironmentModel | null;
  isShiftPressed: boolean;
  isCameraControlsEnabled: boolean;
  clearSelections: () => void;
  setSelectedMeshesForUI: React.Dispatch<React.SetStateAction<THREE.Mesh[]>>;
  debouncedCheckOverlaps: (scene: THREE.Scene) => void;
  isSpatialGridEnabled?: boolean;
}

export const useMouseInteractions = ({
  mountRef,
  refs,
  build,
  environment,
  isShiftPressed,
  isCameraControlsEnabled,
  clearSelections,
  setSelectedMeshesForUI,
  debouncedCheckOverlaps,
  isSpatialGridEnabled,
}: UseMouseInteractionsProps): void => {
  const initialGroupPositionsRef = useRef<{ [uuid: string]: THREE.Vector3 }>(
    {}
  );
  const initialDragPointRef = useRef<THREE.Vector3 | null>(null);

  const handleMouseUp = () => {
    if (refs.isDraggingMesh.current) {
      refs.isDraggingMesh.current = false;
      refs.selectedMesh.current = null;
      initialGroupPositionsRef.current = {};
      initialDragPointRef.current = null;

      if (refs.scene.current) {
        debouncedCheckOverlaps(refs.scene.current);
        snappingService.updateAlignmentLines(refs.scene.current);
      }
    }
  };

  useEffect(() => {
    if (!build || !environment) return;

    const mountElement = mountRef.current;
    if (!mountElement || !refs.scene.current || !refs.camera.current) return;

    const scene = refs.scene.current;
    const camera = refs.camera.current;
    const raycaster = refs.raycaster.current;
    const dragPlane = refs.dragPlane.current;
    const intersectionPoint = refs.intersectionPoint.current;
    const meshOffset = refs.meshOffset.current;

    const getNormalizedMousePosition = (e: MouseEvent) => {
      const rect = mountElement.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
        y: -((e.clientY - rect.top) / rect.height) * 2 + 1,
      };
    };

    const findIntersectedObjects = (e: MouseEvent) => {
      const mousePos = getNormalizedMousePosition(e);
      raycaster.setFromCamera({ x: mousePos.x, y: mousePos.y }, camera);

      const intersects = raycaster.intersectObjects(scene.children, true);
      return intersects.filter(
        (intersect) =>
          intersect.object instanceof THREE.Mesh &&
          intersect.object.userData.isInteractive
      );
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;

      const intersects = findIntersectedObjects(e);

      if (intersects.length > 0) {
        const mesh = intersects[0].object as THREE.Mesh;
        console.log('Clicked mesh:', {
          id: mesh.id,
          uuid: mesh.uuid,
          isQueueLineShopper: mesh.userData.isQueueLineShopper,
          position: mesh.position,
          userData: mesh.userData,
        });

        if (isShiftPressed) {
          const isAlreadySelected = refs.selectedMeshes.current.includes(mesh);
          if (!isAlreadySelected) {
            refs.selectedMeshes.current.push(mesh);
            setSelectedMeshesForUI([...refs.selectedMeshes.current]);
            addSelectionOverlay(
              mesh,
              scene,
              SELECTION_COLOR,
              SELECTION_OPACITY
            );

            if (mesh.userData.isQueueLineShopper) {
              updateShopperBadgeSelection(mesh as ShopperMesh, true);
            }
          } else {
            console.log('Removing from selection');
            refs.selectedMeshes.current = refs.selectedMeshes.current.filter(
              (m) => m !== mesh
            );
            setSelectedMeshesForUI([...refs.selectedMeshes.current]);
            removeSelectionOverlay(mesh, scene);

            if (mesh.userData.isQueueLineShopper) {
              updateShopperBadgeSelection(mesh as ShopperMesh, false);
            }
          }
        } else {
          const isAlreadySelected = refs.selectedMeshes.current.includes(mesh);
          if (!isAlreadySelected) {
            refs.selectedMeshes.current.forEach((existingMesh) => {
              removeSelectionOverlay(existingMesh, scene);
              if (existingMesh.userData.isQueueLineShopper) {
                updateShopperBadgeSelection(existingMesh as ShopperMesh, false);
              }
            });

            clearSelections();
            refs.selectedMeshes.current = [mesh];
            setSelectedMeshesForUI([mesh]);
            addSelectionOverlay(
              mesh,
              scene,
              SELECTION_COLOR,
              SELECTION_OPACITY
            );

            if (mesh.userData.isQueueLineShopper) {
              updateShopperBadgeSelection(mesh as ShopperMesh, true);
            }
          }
        }

        if (
          refs.selectedMeshes.current.length > 0 &&
          !isShiftPressed &&
          !isCameraControlsEnabled
        ) {
          refs.selectedMesh.current = mesh;
          refs.isDraggingMesh.current = true;

          dragPlane.setFromNormalAndCoplanarPoint(
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, 0, 0)
          );

          raycaster.setFromCamera(getNormalizedMousePosition(e), camera);
          if (raycaster.ray.intersectPlane(dragPlane, intersectionPoint)) {
            meshOffset.copy(mesh.position).sub(intersectionPoint);
            initialGroupPositionsRef.current = {};
            refs.selectedMeshes.current.forEach((m) => {
              initialGroupPositionsRef.current[m.uuid] = m.position.clone();
            });
            initialDragPointRef.current = intersectionPoint.clone();
          }
        }
      } else if (!isShiftPressed) {
        refs.selectedMeshes.current.forEach((mesh) => {
          if (mesh.userData.isQueueLineShopper) {
            updateShopperBadgeSelection(mesh as ShopperMesh, false);
          }
        });

        clearSelections();
        refs.selectedMesh.current = null;
        refs.isDraggingMesh.current = false;
        setSelectedMeshesForUI([]);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isCameraControlsEnabled) return;

      if (refs.isDraggingMesh.current && refs.selectedMesh.current) {
        raycaster.setFromCamera(getNormalizedMousePosition(e), camera);

        if (e.shiftKey && refs.selectedMesh.current) {
          const mousePos = getNormalizedMousePosition(e);

          const mesh = refs.selectedMesh.current;
          const meshCenter = mesh.position.clone();
          const meshScreenPos = meshCenter.clone().project(camera);

          const angleToMouse = Math.atan2(
            mousePos.y - meshScreenPos.y,
            mousePos.x - meshScreenPos.x
          );

          const snappedAngle = snappingService.snapRotation(angleToMouse, {
            snapStrength: 0.8,
          });

          mesh.rotation.y = snappedAngle;

          if (mesh.userData.hasOverlay) {
            const overlayUuid = mesh.userData.hasOverlay;
            const overlay = scene.children.find(
              (obj) => obj.uuid === overlayUuid
            ) as THREE.Mesh;
            if (overlay) {
              overlay.rotation.y = mesh.rotation.y;
            }
          }

          return;
        }

        if (raycaster.ray.intersectPlane(dragPlane, intersectionPoint)) {
          if (initialDragPointRef.current) {
            const movement = intersectionPoint
              .clone()
              .sub(initialDragPointRef.current);
            refs.selectedMeshes.current.forEach((mesh) => {
              const initialPos = initialGroupPositionsRef.current[mesh.uuid];
              if (initialPos) {
                const newPosition = initialPos.clone().add(movement);
                let boxWidth, boxHeight;
                if (mesh.userData.isSphere) {
                  const radius = mesh.userData.radius;
                  boxWidth = radius * 2;
                  boxHeight = radius * 2;
                } else if (mesh.userData.isCube) {
                  boxWidth = mesh.userData.width;
                  boxHeight = mesh.userData.depth || mesh.userData.height;
                } else if (mesh.userData.isCircle) {
                  const radius = mesh.userData.radius;
                  boxWidth = radius * 2;
                  boxHeight = radius * 2;
                } else {
                  const geometry = mesh.geometry as THREE.PlaneGeometry;
                  boxWidth = geometry.parameters.width;
                  boxHeight = geometry.parameters.height;
                }
                if (refs.floorPlane.current) {
                  const floorBounds = new THREE.Box3().setFromObject(
                    refs.floorPlane.current
                  );
                  const halfWidth = boxWidth / 2;
                  const halfHeight = boxHeight / 2;
                  newPosition.x = Math.max(
                    floorBounds.min.x + halfWidth,
                    Math.min(floorBounds.max.x - halfWidth, newPosition.x)
                  );
                  newPosition.z = Math.max(
                    floorBounds.min.z + halfHeight,
                    Math.min(floorBounds.max.z - halfHeight, newPosition.z)
                  );
                }
                const originalPosition = mesh.position.clone();
                mesh.position.copy(newPosition);
                const edgeSnappingResult = snappingService.snapToEdge(
                  mesh,
                  scene,
                  {
                    visualize: true,
                    snapStrength: 0.8,
                    snapPoints: ['corners', 'edges'],
                  }
                );
                if (!edgeSnappingResult.snapped) {
                  snappingService.snapAlignment(mesh, scene, {
                    visualize: true,
                    snapStrength: 0.8,
                    snapTypes: ['center', 'edge'],
                  });
                }
                const overlappingObjects = snappingService.checkOverlap(
                  mesh,
                  scene,
                  null,
                  {
                    camera: refs.camera.current ?? undefined,
                    useSpatialGrid: isSpatialGridEnabled,
                    onlyCheckVisible: true,
                  }
                );
                if (overlappingObjects.length > 0) {
                  mesh.position.copy(originalPosition);
                  console.warn('Movement blocked due to collision');
                }
                if (
                  refs.floorPlane.current &&
                  snappingService.checkOutOfBounds(
                    mesh,
                    refs.floorPlane.current
                  )
                ) {
                  mesh.position.copy(originalPosition);
                  console.warn('Movement blocked due to boundary limits');
                }
                if (mesh.userData.hasOverlay) {
                  const overlayUuid = mesh.userData.hasOverlay;
                  const overlay = scene.children.find(
                    (obj) => obj.uuid === overlayUuid
                  ) as THREE.Mesh;
                  if (overlay) {
                    overlay.position.copy(mesh.position);
                    overlay.position.y += 0.001;
                  }
                }
                if (
                  mesh.userData.isQueueLineShopper &&
                  mesh.userData.badgeContainer
                ) {
                  const badgeContainer = mesh.userData
                    .badgeContainer as THREE.Object3D;
                  badgeContainer.position.copy(mesh.position);
                  badgeContainer.position.y = 0.025;
                }
              }
            });
          }
        }

        if (refs.scene.current) {
          snappingService.updateAlignmentLines(refs.scene.current);
        }
      }
    };

    const handleClick = (e: MouseEvent) => {
      if (!refs.isDraggingMesh.current && !isShiftPressed) {
        const intersects = findIntersectedObjects(e);
        if (intersects.length === 0) {
          clearSelections();
          setSelectedMeshesForUI([]);
        }
      }
    };

    mountElement.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    mountElement.addEventListener('click', handleClick);

    return () => {
      mountElement.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      mountElement.removeEventListener('click', handleClick);
    };
  }, [
    isShiftPressed,
    refs.camera,
    refs.dragPlane,
    refs.floorPlane,
    refs.intersectionPoint,
    refs.isDraggingMesh,
    refs.meshOffset,
    refs.raycaster,
    refs.scene,
    refs.selectedMesh,
    refs.selectedMeshes,
    build,
    environment,
    clearSelections,
    isCameraControlsEnabled,
    debouncedCheckOverlaps,
    handleMouseUp,
    setSelectedMeshesForUI,
    mountRef,
    isSpatialGridEnabled,
  ]);
};
